import LoadingStatus from "@imminently/decisively-ui/LoadingStatus";
import { EventListContent } from "@/features/events/components/EventListContent";
import { EventTypeFilter } from "@/features/events/components/EventTypeFilter";
import { EventDetails } from "@/features/events/components/EventDetails";
import { EventTypesButton } from "@/features/events/components/EventTypesButton";
import { EventListSkeleton } from "@/features/events/components/EventListSkeleton";
import { type Event as EventEntry } from "@packages/commons";
import { useHistory } from "react-router-dom";
import { eventDetailsUrl, readEventDetails } from "@/features/events/utils/eventDetails";
import React from "react";
import Icon from "@imminently/decisively-ui/Icon";
import {
  Typography,
  Popover,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import Button from "@imminently/decisively-ui/Button";
import SplitPane from "@imminently/decisively-ui/SplitPane";
import Chip from "@imminently/decisively-ui/Chip";
import ItemList from "@imminently/decisively-ui/ItemList";
import { useInfiniteQuery, useQuery } from "react-query";
import moment from "moment";
import { global } from "@/global";
import SwitchField from "@imminently/decisively-ui/SwitchField";

const PAGE_SIZE = 10;



function normalizePage(raw: any): EventEntry[] {
  return Array.isArray(raw)
    ? raw
    : Array.isArray((raw as any)?.data?.data)
      ? (raw as any).data.data
      : Array.isArray((raw as any)?.data)
        ? (raw as any).data
        : [];
}

interface EventTabProps {
  session: any;
}

type TimeRangeType = "none" | "last5min" | "lastHour" | "custom";
type StatusFilter = "" | "success" | "failure" | "error";

function getTimeRangeStartEnd(
  timeRangeType: TimeRangeType,
  customStart?: string,
  customEnd?: string,
): { start?: string; end?: string } {
  const now = moment.utc();

  switch (timeRangeType) {
    case "last5min":
      return {
        start: now.clone().subtract(5, "minutes").toISOString(),
        end: now.toISOString(),
      };
    case "lastHour":
      return {
        start: now.clone().subtract(1, "hour").toISOString(),
        end: now.toISOString(),
      };
    case "custom":
      if (customStart && customEnd) {
        const startMoment = moment.utc(customStart, "YYYY-MM-DDTHH:mm", true);
        const endMoment = moment.utc(customEnd, "YYYY-MM-DDTHH:mm", true);

        // Ensure start is before end
        if (startMoment.isAfter(endMoment)) {
          return {
            start: endMoment.toISOString(),
            end: startMoment.toISOString(),
          };
        }

        return {
          start: startMoment.toISOString(),
          end: endMoment.toISOString(),
        };
      }
      return {};
    default:
      return {};
  }
}

const EventTab = ({ session }: EventTabProps) => {
  const history = useHistory();
  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(null);

  // Filter state
  const [filterAnchor, setFilterAnchor] = React.useState<HTMLButtonElement | null>(null);
  const [status, setStatus] = React.useState<StatusFilter>("");
  const [eventType, setEventType] = React.useState("");
  const [filterEventType, setFilterEventType] = React.useState("");
  const [start, setStart] = React.useState<string>("");
  const [end, setEnd] = React.useState<string>("");
  // Debug events (e.g. job started/completed) are left out unless this is on.
  const [showDebug, setShowDebug] = React.useState(false);

  // Popover filter state (temporary until applied)
  const [filterStatus, setFilterStatus] = React.useState<StatusFilter>("");
  const [filterTimeRangeType, setFilterTimeRangeType] = React.useState<TimeRangeType>("none");
  const [filterCustomStart, setFilterCustomStart] = React.useState("");
  const [filterCustomEnd, setFilterCustomEnd] = React.useState("");

  // Build URL with filters and pagination
  const buildEventsUrl = (cursor?: string) => {
    const params = new URLSearchParams();
    params.set("$limit", String(PAGE_SIZE));
    if (cursor) params.set("cursor", cursor);
    if (status) params.set("status", status);
    if (eventType) params.set("eventType", eventType);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    if (showDebug) params.set("includeDebug", "true");
    return `sessions/${encodeURIComponent(session.id)}/events?${params.toString()}`;
  };

  // Fetch events through the session index with infinite scroll
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    ["sessions", "events", "by-sessionId", session.id, localStorage.getItem("decisively.tenant"), status || null, eventType || null, start || null, end || null, showDebug],
    ({ pageParam }) => {
      const page = pageParam;
      return global.client.get(buildEventsUrl(page));
    },
    {
      enabled: Boolean(session.id),
      retry: false,
      getNextPageParam: (lastPage: any) => lastPage?.nextCursor ?? lastPage?.data?.nextCursor,
    },
  );

  // Fetch details through the owning session
  const {
    data: serverEventData,
    isLoading: isFetchingEvent,
    error: eventError,
  } = useQuery(
    ["sessions", "events", session.id, selectedEventId, localStorage.getItem("decisively.tenant")],
    () => global.client.get(eventDetailsUrl("session", selectedEventId!, session.id)).then(readEventDetails),
    { enabled: Boolean(selectedEventId), retry: false },
  );

  const entries: EventEntry[] = React.useMemo(() => (data?.pages ?? []).flatMap(normalizePage), [data?.pages]);

  const handleEventSelect = (entry: EventEntry) => {
    const id = entry.id || entry.eventRowKey;
    if (id) setSelectedEventId(String(id));
  };

  const handleRefresh = () => {
    refetch?.();
  };

  const openFilterPopover = (e: React.MouseEvent<HTMLButtonElement>) => {
    setFilterStatus(status);
    setFilterEventType(eventType);
    setFilterTimeRangeType(start || end ? "custom" : "none");
    setFilterCustomStart(start ? moment.utc(start).format("YYYY-MM-DDTHH:mm") : "");
    setFilterCustomEnd(end ? moment.utc(end).format("YYYY-MM-DDTHH:mm") : "");
    setFilterAnchor(e.currentTarget);
  };

  const handleApplyFilters = () => {
    const timeRange = getTimeRangeStartEnd(filterTimeRangeType, filterCustomStart, filterCustomEnd);

    setStatus(filterStatus);
    setEventType(filterEventType);
    setStart(timeRange.start || "");
    setEnd(timeRange.end || "");
    setFilterAnchor(null);
  };

  const handleTimeRangeTypeChange = (newType: TimeRangeType) => {
    setFilterTimeRangeType(newType);
    if (newType !== "custom") {
      setFilterCustomStart("");
      setFilterCustomEnd("");
    }
  };

  // Count active filters for badge display
  const activeFilterCount = [status, eventType, start && end].filter(Boolean).length;

  const renderEventsList = () => {
    if (error) {
      return (
        <Typography
          variant="body2"
          color="error"
          style={{ padding: "1rem" }}
        >
          Error loading events: {(error as Error).message || "Unknown error"}
        </Typography>
      );
    }

    if (entries.length === 0 && hasNextPage) {
      return <div className="p-4">
        <p>No matches in this page.</p>
        <Button label="Search older events" disabled={isFetchingNextPage} onClick={() => fetchNextPage()} />
      </div>;
    }

    if (entries.length === 0) {
      return (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ padding: "1rem" }}
        >
          No events found for this session.
        </Typography>
      );
    }

    return (
      <ItemList
        items={entries}
        skeletonRows={isLoading ? 3 : isFetchingNextPage ? 2 : 0}
        renderSkeletonItem={() => <EventListSkeleton />}
        getItemKey={(entry, index) => String(entry.id || entry.eventRowKey || index)}
        renderItem={(entry, index) => {
          const id = String(entry.id || entry.eventRowKey || index);
          const isSelected = selectedEventId === id;
          const timestamp = entry.timestamp ? moment(entry.timestamp).format("YYYY-MM-DD HH:mm:ss") : "N/A";
          return (
            <ItemList.Item
              selected={isSelected}
              onClick={() => setSelectedEventId(id)}
            >
              <EventListContent eventType={entry.eventType} message={entry.message} timestamp={timestamp} actions={<> <Chip
                  size="sm"
                  variant={
                    entry.status === "success"
                      ? "success"
                      : entry.status === "error"
                        ? "destructive"
                        : entry.status === "warning"
                          ? "warning"
                          : "secondary"
                  }
                  style={{ flexShrink: 0 }}
                >
                  {entry.status ?? "N/A"}
                </Chip> </>} />
            </ItemList.Item>
          );
        }}
        onReachEnd={() => {
          if (hasNextPage && !isFetchingNextPage) fetchNextPage();
        }}
        hasMore={Boolean(hasNextPage)}
        loadingMore={isFetchingNextPage}
      />
    );
  };

  const renderDetailsPanel = () => {
    if (!selectedEventId) {
      return (
        <Typography
          variant="body2"
          color="textSecondary"
        >
          Select an event to view its details
        </Typography>
      );
    }

    if (isFetchingEvent) {
      return (
        <LoadingStatus className="h-full" role="status" message="Loading event details…" />
      );
    }

    if (eventError) {
      return (
        <Typography
          variant="body2"
          color="error"
        >
          Error loading event details: {(eventError as Error).message || "Unknown error"}
        </Typography>
      );
    }

    if (!serverEventData) {
      return (
        <Typography
          variant="body2"
          color="textSecondary"
        >
          No event data available
        </Typography>
      );
    }

    return (
      <EventDetails
        event={{ ...serverEventData, sessionId: serverEventData.sessionId || session.id }}
      />
    );
  };

  return (
    <>
      <SplitPane className="flex-1 min-h-0">
        <SplitPane.CollapsiblePane
          minSize={280}
          preferredSize={320}
          collapsedLabel="Events"
          persistId="session-events"
        >
          <SplitPane.CollapsiblePane.Header>
            <SplitPane.CollapsiblePane.Title>Events</SplitPane.CollapsiblePane.Title>
            <SplitPane.CollapsiblePane.HeaderActions>
              <SwitchField name="session-events-show-debug" label="Debug" value={showDebug} onValueChange={setShowDebug} />
              <EventTypesButton />
              {activeFilterCount > 0 && (
                <Chip
                  size="sm"
                  variant="info"
                  onClick={() => {
                    setStatus("");
                    setStart("");
                    setEnd("");
                  }}
                >{`${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""}`}</Chip>
              )}
              <Button
                icon
                size="sm"
                onClick={openFilterPopover}
                label="Advanced filters"
              >
                <Icon name="filter-list" />
              </Button>
              <Button
                label="Refresh"
                size="sm"
                variant="outline"
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <Icon name="refresh" />
                Refresh
              </Button>
              <SplitPane.CollapsiblePane.ToggleButton size="sm" />
            </SplitPane.CollapsiblePane.HeaderActions>
          </SplitPane.CollapsiblePane.Header>
          <SplitPane.CollapsiblePane.Content className="!overflow-hidden bg-white">
            {renderEventsList()}
          </SplitPane.CollapsiblePane.Content>
        </SplitPane.CollapsiblePane>
        <SplitPane.Pane className="min-h-0 overflow-hidden bg-white">{renderDetailsPanel()}</SplitPane.Pane>
      </SplitPane>

      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disablePortal={false}
        container={() => document.body}
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        style={{ zIndex: 1400, pointerEvents: "none" }}
        PaperProps={{
          style: { zIndex: 1400, pointerEvents: "auto" },
          onMouseDown: (e) => {
            e.stopPropagation();
          },
        }}
      >
        <div
          className="p-2 flex flex-col gap-3"
          style={{ minWidth: 280 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 500 }}
          >
            Advanced Filters
          </Typography>
          <EventTypeFilter
            value={filterEventType}
            onValueChange={setFilterEventType}
          />
          <FormControl
            fullWidth
            size="small"
            variant="outlined"
          >
            <InputLabel id="filter-status-label">Status</InputLabel>
            <Select
              labelId="filter-status-label"
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus((e.target.value as StatusFilter) || "")}
              MenuProps={{
                style: { zIndex: 1500, pointerEvents: "auto" },
                disablePortal: false,
                disableEnforceFocus: true,
                disableAutoFocus: true,
                disableRestoreFocus: true,
                container: () => document.body,
                PaperProps: {
                  style: { zIndex: 1500, pointerEvents: "auto" },
                  onMouseDown: (e) => e.stopPropagation(),
                },
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failure">Failure</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>
          <Divider style={{ marginTop: 8, marginBottom: 8 }} />
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 500, marginBottom: 8 }}
          >
            Time Range (UTC+0)
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={filterTimeRangeType}
              onChange={(e) => handleTimeRangeTypeChange(e.target.value as TimeRangeType)}
            >
              <FormControlLabel
                value="none"
                control={<Radio size="small" />}
                label="No time filter"
              />
              <FormControlLabel
                value="last5min"
                control={<Radio size="small" />}
                label="Last 5 minutes"
              />
              <FormControlLabel
                value="lastHour"
                control={<Radio size="small" />}
                label="Last hour"
              />
              <FormControlLabel
                value="custom"
                control={<Radio size="small" />}
                label="Custom range"
              />
            </RadioGroup>
          </FormControl>
          {filterTimeRangeType === "custom" && (
            <div className="flex flex-col gap-3 mt-2">
              <TextField
                label="Start Time (UTC)"
                type="datetime-local"
                value={filterCustomStart}
                onChange={(e) => setFilterCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End Time (UTC)"
                type="datetime-local"
                value={filterCustomEnd}
                onChange={(e) => setFilterCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </div>
          )}
          <div className="flex gap-2 justify-end mt-2">
            <Button
              label="Cancel"
              size="sm"
              onClick={() => setFilterAnchor(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="contained"
              onClick={handleApplyFilters}
              label="Apply"
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default EventTab;
